import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { ProgrammeAlignment, formatProgrammeDate } from "@/lib/programmeAlignment";

export type RealignChoice = "move_start" | "shift_items";

interface RealignDaysDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alignment: ProgrammeAlignment;
  startDate?: string | null;
  endDate?: string | null;
  isSaving?: boolean;
  onConfirm: (choice: RealignChoice, trimEnd: boolean) => void;
}

/**
 * Offers to trim empty leading days from a programme, either by moving the
 * programme's start date forward (renumbering days) or by shifting every
 * item earlier.
 */
export function RealignDaysDialog({
  open,
  onOpenChange,
  alignment,
  startDate,
  endDate,
  isSaving = false,
  onConfirm,
}: RealignDaysDialogProps) {
  const [choice, setChoice] = useState<RealignChoice>("move_start");
  const [trimEnd, setTrimEnd] = useState(true);

  useEffect(() => {
    if (open) {
      setChoice("move_start");
      setTrimEnd(alignment.trailingEmptyDays > 0);
    }
  }, [open, alignment.trailingEmptyDays]);

  const { leadingEmptyDays, trailingEmptyDays, firstItemDate, lastItemDate } = alignment;
  const dayWord = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Realign programme days</DialogTitle>
          <DialogDescription>
            {startDate && endDate
              ? `The programme runs ${formatProgrammeDate(startDate)} – ${formatProgrammeDate(endDate)}.`
              : "The programme's travel dates and its items don't line up."}
            {firstItemDate && lastItemDate
              ? ` Items run ${formatProgrammeDate(firstItemDate)} – ${formatProgrammeDate(lastItemDate)}.`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {leadingEmptyDays > 0 && (
            <p className="text-sm text-muted-foreground">
              The first {dayWord(leadingEmptyDays)} of the programme {leadingEmptyDays === 1 ? "has" : "have"} no items.
            </p>
          )}

          {leadingEmptyDays > 0 && (
            <RadioGroup value={choice} onValueChange={(v) => setChoice(v as RealignChoice)} className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="move_start" id="realign-move-start" className="mt-1" />
                <Label htmlFor="realign-move-start" className="font-normal cursor-pointer">
                  <span className="font-medium">
                    Start the programme on {firstItemDate ? formatProgrammeDate(firstItemDate) : "the first day with items"}
                  </span>
                  <span className="block text-sm text-muted-foreground mt-1">
                    Recommended. That day becomes Day 1 and the rest renumber automatically. No item dates change.
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="shift_items" id="realign-shift" className="mt-1" />
                <Label htmlFor="realign-shift" className="font-normal cursor-pointer">
                  <span className="font-medium">Move everything {dayWord(leadingEmptyDays)} earlier</span>
                  <span className="block text-sm text-muted-foreground mt-1">
                    Keeps {startDate ? formatProgrammeDate(startDate) : "the current start date"} as Day 1 and shifts every
                    item back, hotels included, keeping the same number of nights.
                  </span>
                </Label>
              </div>
            </RadioGroup>
          )}

          {trailingEmptyDays > 0 && (
            <div className="flex items-start gap-3 rounded-md border p-3">
              <Checkbox
                id="realign-trim-end"
                checked={trimEnd}
                onCheckedChange={(c) => setTrimEnd(!!c)}
                className="mt-1"
              />
              <Label htmlFor="realign-trim-end" className="font-normal cursor-pointer">
                <span className="font-medium">
                  Also end the programme on {lastItemDate ? formatProgrammeDate(lastItemDate) : "the last day with items"}
                </span>
                <span className="block text-sm text-muted-foreground mt-1">
                  The last {dayWord(trailingEmptyDays)} of the programme {trailingEmptyDays === 1 ? "has" : "have"} no items.
                </span>
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(choice, trimEnd)} disabled={isSaving}>
            {isSaving ? "Saving..." : "Realign days"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
